import { IsTileConstructor } from "../tiles.interfaces";
import { EventState, Observable } from "../../events/events.observable";
import { TileCollection } from "../tiles.collection";
import { TileBuilder } from "../tiles.builder";
import { TilePipelineLink } from "../pipeline";
import { ValidableBase } from "../../validable";
export class AbstractTileProvider extends ValidableBase {
    constructor(factory, enabled = true) {
        super();
        this._links = [];
        if (factory && IsTileConstructor(factory)) {
            this._factory = this._buildFactory(factory) ?? this._buildFactoryInternal(factory);
        }
        else {
            this._factory = factory ?? this._buildFactory() ?? this._buildFactoryInternal();
        }
        this._enabled = enabled;
        this._activTiles = new TileCollection();
        this._callback = this._onContentFetched.bind(this);
    }
    get geoBounds() {
        return this._activTiles?.geoBounds;
    }
    get boundingBox() {
        return this._activTiles?.boundingBox;
    }
    get enabledObservable() {
        this._enabledObservable = this._enabledObservable || new Observable();
        return this._enabledObservable;
    }
    get enabled() {
        return this._enabled;
    }
    set enabled(v) {
        if (this._enabled !== v) {
            this._enabled = v;
            if (this._enabledObservable && this._enabledObservable.hasObservers()) {
                this._enabledObservable.notifyObservers(this, -1, this, this);
            }
        }
    }
    get metrics() {
        return this.factory.metrics;
    }
    get namespace() {
        return this.factory.namespace;
    }
    get factory() {
        return this._factory;
    }
    dispose() {
        this._activTiles?.clear();
    }
    get activTiles() {
        return Array.from(this._activTiles);
    }
    getTile(a) {
        return this._activTiles.get(a);
    }
    hasTile(a) {
        return this._activTiles.has(a);
    }
    get updatedObservable() {
        this._updateObservable = this._updateObservable || new Observable();
        return this._updateObservable;
    }
    get addedObservable() {
        this._addedObservable = this._addedObservable || new Observable();
        return this._addedObservable;
    }
    get removedObservable() {
        this._removedObservable = this._removedObservable || new Observable();
        return this._removedObservable;
    }
    linkTo(target, options, ...args) {
        if (this._links.findIndex((l) => l.target === target) === -1) {
            const link = new TilePipelineLink(this, target, options);
            this._links.push(link);
            this._onLinked(link);
        }
    }
    _onLinked(link) {
        link.forwardAdded(Array.from(this._activTiles), new EventState(-1, false, this, this));
    }
    unlinkFrom(target) {
        const i = this._links.findIndex((l) => l.target === target);
        if (i !== -1) {
            const l = this._links.splice(i)[0];
            this._onUnlinked(l);
            l.dispose();
            return l;
        }
        return undefined;
    }
    _onUnlinked(link) { }
    added(eventData, eventState) {
        const tiles = this._onTileAddressesAdded(eventData, eventState);
        if (tiles.length) {
            this.invalidate();
            if (tiles.length === 1) {
                this._onTileAdded(tiles[0]);
            }
            else {
                this._onTilesAdded(tiles);
            }
            if (this._addedObservable && this._addedObservable.hasObservers()) {
                this._addedObservable.notifyObservers(tiles, -1, this, this);
            }
        }
    }
    removed(eventData, eventState) {
        const tiles = this._onTileAddressesRemoved(eventData, eventState);
        if (tiles.length) {
            this.invalidate();
            if (tiles.length === 1) {
                this._onTileRemoved(tiles[0]);
            }
            else {
                this._onTilesRemoved(tiles);
            }
            if (this._removedObservable && this._removedObservable.hasObservers()) {
                this._removedObservable?.notifyObservers(tiles, -1, this, this);
            }
        }
    }
    updated(eventData, eventState) {
    }
    _onTileAddressesAdded(address, eventState) {
        const toActivate = address.length === 0 ? [...(this._activTiles ?? [])].map((t) => t.address) : address;
        const tiles = new Array();
        for (const a of toActivate ?? []) {
            const t = this._activTiles?.get(a);
            if (t) {
                tiles.push(t);
                continue;
            }
            const factory = this._factory.withAddress(a);
            try {
                let tile = factory.build();
                if (tile) {
                    tile = this._fetchContent(tile, this._callback);
                    this._activTiles?.add(tile);
                    tiles.push(tile);
                }
            }
            catch (e) {
                console.log(e);
            }
        }
        return tiles;
    }
    _onTileAddressesRemoved(address, eventState) {
        if (this._activTiles && this._activTiles.count) {
            const tiles = new Array();
            for (const a of address ?? []) {
                const t = this._activTiles?.get(a);
                if (t) {
                    tiles.push(t);
                    this._activTiles?.remove(a);
                }
            }
            return tiles;
        }
        return [];
    }
    _onContentFetched(tile) {
        this.invalidate();
        this._onTileUpdated(tile);
        if (this.updatedObservable?.hasObservers()) {
            this.updatedObservable.notifyObservers([tile], -1, this, this);
        }
    }
    _buildFactory(type) {
        return this._buildFactoryInternal(type);
    }
    _onTilesAdded(tiles) {
        for (const t of tiles) {
            this._onTileAdded(t);
        }
    }
    _onTileAdded(tiles) { }
    _onTilesRemoved(tiles) {
        for (const t of tiles) {
            this._onTileRemoved(t);
        }
    }
    _onTileRemoved(tiles) { }
    _onTilesUpdated(tiles) {
        for (const t of tiles) {
            this._onTileUpdated(t);
        }
    }
    _onTileUpdated(tiles) { }
    _buildFactoryInternal(type) {
        const b = new TileBuilder();
        return type ? b.withType(type) : b;
    }
}
//# sourceMappingURL=tiles.provider.abstract.js.map